import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import EntityMemberSchema from "_shared/entity-members/entity-member-schema";
import FeeScheduleCategorySchema from "_shared/fee-schedules/categories/fee-schedule-category-schema";
import FeeScheduleTag from "_shared/fee-schedules/tags/fee-schedule-tag";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type FeeScheduleTemplateJSON = z.infer<typeof FeeScheduleTemplateSchema.FeeScheduleTemplateJSONZ>;
export type FeeScheduleTemplateParams = z.infer<typeof FeeScheduleTemplateSchema.FeeScheduleTemplateZ>;

export default class FeeScheduleTemplateSchema {
  public static readonly FeeScheduleTemplateJSONZ = z.object({
    id: z.uuid().nullish(),
    name: z.string(),
    tags: z.array(z.string()).nullish(),
    categories: z.array(FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ).nullish(),
    created: z.iso.datetime({ offset: true }).nullish(),
    creator: EntityMemberSchema.EntityMemberJSONZ.nullish(),
    archived: DateSchema.type.nullish(),
  });

  public static readonly FeeScheduleTemplateZ = z.object({
    id: z.instanceof(Guid).optional(),
    name: z.string(),
    tags: z.array(z.instanceof(FeeScheduleTag)).optional(),
    categories: z.array(FeeScheduleCategorySchema.FeeScheduleCategoryZ).optional(),
    created: DateSchema.type.optional(),
    creator: EntityMemberSchema.EntityMemberZ.optional(),
    archived: DateSchema.type.optional(),
  });

  public static validate(values: unknown): FeeScheduleTemplateParams {
    return parseOrThrow(FeeScheduleTemplateSchema.FeeScheduleTemplateZ, values, {
      logError: true,
      schemaName: "FeeScheduleTemplateSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): FeeScheduleTemplateJSON {
    return parseOrThrow(FeeScheduleTemplateSchema.FeeScheduleTemplateJSONZ, json, {
      logError: true,
      schemaName: "FeeScheduleTemplateSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is FeeScheduleTemplateParams {
    try {
      FeeScheduleTemplateSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is FeeScheduleTemplateJSON {
    try {
      FeeScheduleTemplateSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: FeeScheduleTemplateJSON): FeeScheduleTemplateParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(FeeScheduleTemplateSchema.FeeScheduleTemplateJSONZ, json, {
      logError: true,
      schemaName: "FeeScheduleTemplateSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return FeeScheduleTemplateSchema.codec.decode(parsed);
  }

  public static serialize(FeeScheduleTemplate: FeeScheduleTemplateParams): FeeScheduleTemplateJSON {
    if (isNullOrUndefined(FeeScheduleTemplate)) return FeeScheduleTemplate;
    const parsed = FeeScheduleTemplateSchema.validate(FeeScheduleTemplate);
    if (isNullOrUndefined(parsed)) return parsed;
    return FeeScheduleTemplateSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    FeeScheduleTemplateSchema.FeeScheduleTemplateJSONZ,
    FeeScheduleTemplateSchema.FeeScheduleTemplateZ,
    {
      decode: (data) => ({
        id: data.id ? new Guid(data.id) : undefined,
        created: new AHDate(data.created ?? undefined),
        creator: data.creator ? EntityMemberSchema.deserialize(data.creator) : undefined,
        archived: data.archived ?? undefined,
        tags: data.tags ? data.tags.map((tag) => new FeeScheduleTag(tag)) : [],
        name: data.name,
        categories: data.categories ? data.categories.map(FeeScheduleCategorySchema.deserialize) : [],
      }),
      encode: (FeeScheduleTemplate) => ({
        id: FeeScheduleTemplate.id?.value ?? "",
        created: FeeScheduleTemplate.created?.toISOString() ?? "",
        creator: FeeScheduleTemplate.creator ? EntityMemberSchema.serialize(FeeScheduleTemplate.creator) : undefined,
        archived: FeeScheduleTemplate.archived ?? undefined,
        tags: FeeScheduleTemplate.tags ? FeeScheduleTemplate.tags.map((tag) => tag.value) : [],
        name: FeeScheduleTemplate.name,
        categories: FeeScheduleTemplate.categories
          ? FeeScheduleTemplate.categories.map(FeeScheduleCategorySchema.serialize)
          : [],
      }),
    }
  );
}
